import { Document, Packer, Paragraph, TextRun, Table, TableCell, TableRow, WidthType, AlignmentType, BorderStyle, PageOrientation, VerticalAlign, HeightRule, TableLayoutType } from 'docx';
import { jsPDF } from 'jspdf';
import { registerPdfFont } from './pdfFont.js';
import { computePlannerStats } from './plannerStats.js';

export const MONTHS_RU = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
];

const DAY_LABELS = ['ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ', 'ВС'];

function pad(n) {
  return String(n).padStart(2, '0');
}

function rgbFromHex(hex) {
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16)
  ];
}

function formatTime(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function getEventDates(event, objectId) {
  const od = event.objectDates && event.objectDates[objectId];
  return {
    start: new Date(od && od.startDate ? od.startDate : event.startDate),
    end: new Date(od && od.endDate ? od.endDate : event.endDate)
  };
}

function dayInRange(day, start, end) {
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;
  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0);
  const dayEnd = new Date(dayStart);
  dayEnd.setHours(23, 59, 59, 999);
  return start <= dayEnd && end >= dayStart;
}

// Собираем события дня: objectId === null — по всем объектам (для админа)
function collectDayEvents(objects, objectId, day) {
  const targets = objectId ? objects.filter((o) => String(o.id) === String(objectId)) : objects;
  const list = [];
  for (const obj of targets) {
    for (const ev of obj.events || []) {
      const { start, end } = getEventDates(ev, obj.id);
      if (!dayInRange(day, start, end)) continue;
      list.push({
        id: ev.id,
        name: ev.name,
        timeStart: formatTime(start),
        timeEnd: formatTime(end),
        objectName: obj.name,
        status: ev.status === 'approved' ? 'approved' : 'not_approved'
      });
    }
  }
  if (!objectId) {
    const seen = new Set();
    const unique = list.filter((e) => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    });
    return unique.sort((a, b) => a.timeStart.localeCompare(b.timeStart));
  }
  return list.sort((a, b) => a.timeStart.localeCompare(b.timeStart));
}

// Сетка месяца: недели Пн–Вс, ячейки только реальные дни месяца
// holidaySet — Set дат 'YYYY-MM-DD' из календаря приложения (isdayoff.ru)
export function buildPlannerGrid(year, month, objects, objectId, holidaySet) {
  const first = new Date(year, month, 1);
  const dayOfWeek = first.getDay();
  const toMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(year, month, 1 + toMonday);
  const lastDay = new Date(year, month + 1, 0);

  const weeks = [];
  let cursor = new Date(monday);
  while (cursor <= lastDay) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(cursor);
      const inMonth = day.getMonth() === month;
      const dw = day.getDay();
      const isWeekend = dw === 0 || dw === 6;
      const dateStr = `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
      week.push({
        date: day,
        inMonth,
        dayNumber: inMonth ? day.getDate() : null,
        isWeekend,
        isHoliday: holidaySet ? holidaySet.has(dateStr) : false,
        events: inMonth ? collectDayEvents(objects, objectId, day) : []
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
}

// Период: месяцы с (startYear, startMonth) по (endYear, endMonth) включительно — массив месячных сеток
export function buildPlannerPeriodGrids(startYear, startMonth, endYear, endMonth, objects, objectId, holidaySet) {
  const grids = [];
  const cursor = new Date(startYear, startMonth, 1);
  const end = new Date(endYear, endMonth, 1);
  while (cursor <= end) {
    grids.push({
      year: cursor.getFullYear(),
      month: cursor.getMonth(),
      weeks: buildPlannerGrid(cursor.getFullYear(), cursor.getMonth(), objects, objectId, holidaySet)
    });
    cursor.setFullYear(cursor.getFullYear() + (cursor.getMonth() === 11 ? 1 : 0));
    cursor.setMonth((cursor.getMonth() + 1) % 12);
  }
  return grids;
}

// Разделы по всем объектам для выгрузки «Все объекты»: [{ objectName, grids }]
// Каждый объект — свои месячные сетки с учетом objectDates[objId]
export function buildPlannerSections(startYear, startMonth, endYear, endMonth, objects, holidaySet) {
  return objects
    .filter((o) => o && o.name)
    .map((o) => ({
      objectName: o.name,
      grids: buildPlannerPeriodGrids(startYear, startMonth, endYear, endMonth, objects, o.id, holidaySet)
    }));
}

export function formatMonthsPeriod(grids) {
  if (grids.length === 1) return `${MONTHS_RU[grids[0].month].toUpperCase()} ${grids[0].year}`;
  const first = grids[0];
  const last = grids[grids.length - 1];
  return `ПЛАНЁР: ${MONTHS_RU[first.month].toUpperCase()} ${first.year} — ${MONTHS_RU[last.month].toUpperCase()} ${last.year}`;
}

export function plannerFilename(grids, ext) {
  if (grids.length === 1) {
    return `планёр_${MONTHS_RU[grids[0].month].toLowerCase()}_${grids[0].year}.${ext}`;
  }
  const first = grids[0];
  const last = grids[grids.length - 1];
  const years = first.year === last.year ? `${first.year}` : `${first.year}-${last.year}`;
  return `планёр_${MONTHS_RU[first.month].toLowerCase()}-${MONTHS_RU[last.month].toLowerCase()}_${years}.${ext}`;
}

function eventLine(e) {
  const time = e.timeStart && e.timeEnd ? `${e.timeStart}-${e.timeEnd}` : (e.timeStart || e.timeEnd || '');
  return time ? `${time} ${e.name}` : e.name;
}

// Неутверждённые (черновики) — янтарная заливка, утверждённые — зелёная; текст остаётся чёрным
const STATUS_BG_HEX = { approved: 'E8F5E9', not_approved: 'FFF3E0' };
const STATUS_BG_RGB = { approved: [232, 245, 233], not_approved: [255, 243, 224] };

function eventStatusBg(e) {
  return e.status === 'approved' ? STATUS_BG_HEX.approved : STATUS_BG_HEX.not_approved;
}

function eventStatusBgRgb(e) {
  return e.status === 'approved' ? STATUS_BG_RGB.approved : STATUS_BG_RGB.not_approved;
}

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

const CELL_BORDERS = {
  top: { style: BorderStyle.SINGLE, size: 4, color: '999999' },
  bottom: { style: BorderStyle.SINGLE, size: 4, color: '999999' },
  left: { style: BorderStyle.SINGLE, size: 4, color: '999999' },
  right: { style: BorderStyle.SINGLE, size: 4, color: '999999' }
};

function buildWordTable(weeks, cellWidth) {
  // Заголовок ПН–ВС
  const headerRow = new TableRow({
    height: { value: 380, rule: HeightRule.ATLEAST },
    children: DAY_LABELS.map((label) => new TableCell({
      width: { size: cellWidth, type: WidthType.DXA },
      verticalAlign: VerticalAlign.CENTER,
      shading: { fill: 'EFEFEF' },
      borders: CELL_BORDERS,
      children: [new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [new TextRun({ text: label, bold: true, size: 18 })]
      })]
    }))
  });

  const weekRows = weeks.map((week) => new TableRow({
    height: { value: 1500, rule: HeightRule.ATLEAST },
    children: week.map((cell) => {
      const children = [];
      if (cell.inMonth) {
        const dayColored = cell.isHoliday || cell.isWeekend;
        children.push(new Paragraph({
          spacing: { after: 40 },
          children: [new TextRun({ text: String(cell.dayNumber), bold: true, size: 20, color: dayColored ? 'D32F2F' : undefined })]
        }));
        cell.events.forEach((e) => {
          children.push(new Paragraph({
            spacing: { after: 20 },
            shading: { fill: eventStatusBg(e) },
            children: [new TextRun({ text: eventLine(e), size: 16 })]
          }));
        });
      }
      return new TableCell({
        width: { size: cellWidth, type: WidthType.DXA },
        verticalAlign: VerticalAlign.TOP,
        borders: CELL_BORDERS,
        shading: !cell.inMonth
          ? { fill: 'FAFAFA' }
          : cell.isHoliday
            ? { fill: 'FFCDD2' }
            : cell.isWeekend
              ? { fill: 'FFEBEE' }
              : undefined,
        children
      });
    })
  }));

  return new Table({
    width: { size: cellWidth * 7, type: WidthType.DXA },
    columnWidths: Array(7).fill(cellWidth),
    rows: [headerRow, ...weekRows],
    layout: TableLayoutType.FIXED
  });
}

function buildWordStatsTable(stats) {
  const headerCell = (text) => new TableCell({
    width: { size: 3000, type: WidthType.DXA },
    verticalAlign: VerticalAlign.CENTER,
    shading: { fill: 'EFEFEF' },
    borders: CELL_BORDERS,
    children: [new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text, bold: true, size: 18 })]
    })]
  });
  const dataCell = (text, bold = false, total = false) => new TableCell({
    width: { size: 3000, type: WidthType.DXA },
    verticalAlign: VerticalAlign.CENTER,
    shading: total ? { fill: 'EFEFEF' } : undefined,
    borders: CELL_BORDERS,
    children: [new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text: String(text), bold, size: 18 })]
    })]
  });
  const rows = [new TableRow({
    children: [
      headerCell('Месяц'),
      headerCell('Всего'),
      headerCell('Утверждённых'),
      headerCell('Черновиков')
    ]
  })];
  stats.rows.forEach((r) => {
    rows.push(new TableRow({
      children: [
        dataCell(`${MONTHS_RU[r.month]} ${r.year}`),
        dataCell(r.total),
        dataCell(r.approved),
        dataCell(r.drafts)
      ]
    }));
  });
  rows.push(new TableRow({
    children: [
      dataCell('Итого за период', true, true),
      dataCell(stats.total.total, true, true),
      dataCell(stats.total.approved, true, true),
      dataCell(stats.total.drafts, true, true)
    ]
  }));
  return new Table({
    width: { size: 12000, type: WidthType.DXA },
    columnWidths: Array(4).fill(3000),
    rows,
    layout: TableLayoutType.FIXED
  });
}

function buildMonthTitleParagraph(grid, pageBreak) {
  return new Paragraph({
    ...(pageBreak ? { pageBreakBefore: true } : {}),
    alignment: AlignmentType.CENTER,
    spacing: { after: 240 },
    children: [new TextRun({ text: `${MONTHS_RU[grid.month].toUpperCase()} ${grid.year}`, bold: true, size: 32 })]
  });
}

function buildLegendParagraph() {
  return new Paragraph({
    spacing: { before: 120 },
    children: [new TextRun({ text: 'Обозначения: ', size: 16, italic: true }),
      new TextRun({ text: '   ', size: 16, shading: { fill: 'FFEBEE' } }),
      new TextRun({ text: ' выходной   ', size: 16 }),
      new TextRun({ text: '   ', size: 16, shading: { fill: 'FFCDD2' } }),
      new TextRun({ text: ' праздничный день   ', size: 16 }),
      new TextRun({ text: '   ', size: 16, shading: { fill: 'E8F5E9' } }),
      new TextRun({ text: ' утверждённое мероприятие   ', size: 16 }),
      new TextRun({ text: '   ', size: 16, shading: { fill: 'FFF3E0' } }),
      new TextRun({ text: ' черновик', size: 16 })]
  });
}

function buildObjectParagraph(objectName) {
  return new Paragraph({
    spacing: { before: 60 },
    children: [new TextRun({ text: `Объект: ${objectName}`, size: 18, italics: true })]
  });
}

function appendWordSection(months, grids, cellWidth, { objectName, isFirstPage }) {
  grids.forEach((grid, i) => {
    months.push(buildMonthTitleParagraph(grid, i > 0 || !isFirstPage));
    months.push(buildWordTable(grid.weeks, cellWidth));
    if (i === 0) {
      if (isFirstPage) months.push(buildLegendParagraph());
      if (objectName) months.push(buildObjectParagraph(objectName));
    }
  });
}

function buildStatsTitleParagraphs(grids, label) {
  const out = [];
  if (grids.length > 1) {
    out.push(new Paragraph({
      pageBreakBefore: true,
      alignment: AlignmentType.CENTER,
      spacing: { before: 120, after: 240 },
      children: [new TextRun({ text: 'Статистика мероприятий', bold: true, size: 24 })]
    }));
  } else {
    out.push(new Paragraph({
      spacing: { before: 240, after: 80 },
      children: [new TextRun({ text: 'Статистика мероприятий', bold: true, size: 24 })]
    }));
  }
  out.push(new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { after: 120 },
    children: [new TextRun({
      text: `Период: ${formatMonthsPeriod(grids)}${label ? ` (${label})` : ''}`,
      size: 16,
      italics: true
    })]
  }));
  return out;
}

// Выгрузка: один объект — { grids, objectName }; все объекты — { sections, mergedGrids, objectName }
export async function exportPlannerWord(opts) {
  const cellWidth = Math.floor(16038 / 7);
  const months = [];
  const sections = opts.sections && opts.sections.length ? opts.sections : null;
  const refGrids = sections ? sections[0].grids : opts.grids;

  if (sections) {
    if (refGrids.length > 1) {
      months.push(new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [new TextRun({ text: formatMonthsPeriod(refGrids), bold: true, size: 28 })]
      }));
    }
    sections.forEach((s, si) => {
      appendWordSection(months, s.grids, cellWidth, { objectName: s.objectName, isFirstPage: si === 0 });
    });
    months.push(...buildStatsTitleParagraphs(refGrids, opts.objectName));
    months.push(buildWordStatsTable(computePlannerStats(opts.mergedGrids)));
  } else {
    if (opts.grids.length > 1) {
      months.push(new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [new TextRun({ text: formatMonthsPeriod(opts.grids), bold: true, size: 28 })]
      }));
    }
    opts.grids.forEach((grid, i) => {
      months.push(buildMonthTitleParagraph(grid, i > 0));
      months.push(buildWordTable(grid.weeks, cellWidth));
      if (i === 0) {
        months.push(buildLegendParagraph());
        if (opts.objectName) months.push(buildObjectParagraph(opts.objectName));
      }
    });
    months.push(...buildStatsTitleParagraphs(opts.grids, opts.objectName));
    months.push(buildWordStatsTable(computePlannerStats(opts.grids)));
  }

  const doc = new Document({
    styles: { default: { document: { run: { font: 'Cambria' } } } },
    sections: [{
      properties: {
        page: {
          size: { width: 16838, height: 11906, orientation: PageOrientation.LANDSCAPE },
          margin: { top: 400, bottom: 400, left: 400, right: 400 }
        }
      },
      children: months
    }]
  });
  const blob = await Packer.toBlob(doc);
  downloadBlob(plannerFilename(refGrids, 'docx'), blob);
}

function renderPdfMonth(doc, grid, objectName, showObject) {
  const pageW = 297;
  const margin = 8;
  const cellW = (pageW - margin * 2) / 7;
  const pageH = 210;

  doc.setFontSize(16);
  doc.setFont('PTSans', 'bold');
  doc.text(`${MONTHS_RU[grid.month].toUpperCase()} ${grid.year}`, pageW / 2, 12, { align: 'center' });
  if (showObject && objectName) {
    doc.setFontSize(10);
    doc.setFont('PTSans', 'italic');
    doc.text(`Объект: ${objectName}`, pageW / 2, 18, { align: 'center' });
  }

  let y = 22;
  const headerH = 7;
  DAY_LABELS.forEach((label, i) => {
    const x = margin + i * cellW;
    doc.setFillColor(235, 235, 235);
    doc.rect(x, y, cellW, headerH, 'F');
    doc.setDrawColor(150, 150, 150);
    doc.setLineWidth(0.2);
    doc.rect(x, y, cellW, headerH, 'S');
    doc.setFontSize(8);
    doc.setFont('PTSans', 'bold');
    doc.text(label, x + cellW / 2, y + headerH / 2 + 1, { align: 'center' });
  });
  y += headerH;

  const weeks = grid.weeks;
  const rowsH = Math.min(26, (pageH - y - 2) / weeks.length);
  weeks.forEach((week) => {
    week.forEach((cell, i) => {
      const x = margin + i * cellW;
      if (cell.inMonth && (cell.isHoliday || cell.isWeekend)) {
        if (cell.isHoliday) doc.setFillColor(255, 205, 210);
        else if (cell.isWeekend) doc.setFillColor(255, 235, 238);
        doc.rect(x, y, cellW, rowsH, 'F');
      }
      doc.setDrawColor(150, 150, 150);
      doc.setLineWidth(0.2);
      doc.rect(x, y, cellW, rowsH, 'S');
      if (!cell.inMonth) return;

      doc.setFontSize(9);
      doc.setFont('PTSans', 'bold');
      if (cell.isHoliday || cell.isWeekend) doc.setTextColor(211, 47, 47);
      doc.text(String(cell.dayNumber), x + 2, y + 5);
      doc.setTextColor(0, 0, 0);

      let ty = y + 9;
      doc.setFontSize(6.5);
      doc.setFont('PTSans', 'normal');
      for (const e of cell.events) {
        if (ty > y + rowsH - 3) break;
        const lines = doc.splitTextToSize(eventLine(e), cellW - 3);
        const room = y + rowsH - 3 - ty;
        const drawable = Math.min(lines.length, Math.max(0, Math.floor(room / 3.4)));
        if (drawable <= 0) break;
        const [r, g, b] = eventStatusBgRgb(e);
        doc.setFillColor(r, g, b);
        doc.rect(x + 1.5, ty - 2.2, cellW - 3, drawable * 3.4, 'F');
        doc.setTextColor(0, 0, 0);
        for (let i = 0; i < drawable; i++) {
          doc.text(lines[i], x + 1.5, ty);
          ty += 3.4;
        }
      }
    });
    y += rowsH;
  });
}

function renderPdfLegend(doc, y) {
  doc.setFontSize(7);
  doc.setFont('PTSans', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text('Обозначения:', 8, y + 1.9);
  const items = [
    ['FFEBEE', 'выходной'],
    ['FFCDD2', 'праздничный день'],
    ['E8F5E9', 'утверждённое мероприятие'],
    ['FFF3E0', 'черновик']
  ];
  let x = 8 + doc.getTextWidth('Обозначения:') + 4;
  for (const [hex, label] of items) {
    const rgb = rgbFromHex(hex);
    doc.setFillColor(rgb[0], rgb[1], rgb[2]);
    doc.rect(x, y, 3, 2.6, 'F');
    doc.setDrawColor(150, 150, 150);
    doc.setLineWidth(0.1);
    doc.rect(x, y, 3, 2.6, 'S');
    doc.setTextColor(0, 0, 0);
    doc.text(label, x + 3.5, y + 1.9);
    x = x + 3.5 + doc.getTextWidth(label) + 5;
  }
}

function renderPdfStats(doc, stats, objectName, label) {
  const pageW = 297;
  const colW = [120, 42, 58, 53];
  const tableW = colW.reduce((a, b) => a + b, 0);
  const startX = (pageW - tableW) / 2;

  const drawRow = (values, bold, shade) => {
    let x = startX;
    const rowH = 9;
    if (shade) {
      doc.setFillColor(235, 235, 235);
      doc.rect(x, y, tableW, rowH, 'F');
    }
    values.forEach((text, i) => {
      doc.setDrawColor(150, 150, 150);
      doc.setLineWidth(0.2);
      doc.rect(x, y, colW[i], rowH, 'S');
      doc.setFontSize(9);
      doc.setFont('PTSans', bold ? 'bold' : 'normal');
      const alignX = i === 0 ? x + 2 : x + colW[i] / 2;
      doc.text(String(text), alignX, y + rowH / 2 + 1, { align: i === 0 ? 'left' : 'center' });
      x += colW[i];
    });
    y += rowH;
  };

  const title = `Статистика мероприятий — ${label}${objectName ? ` (${objectName})` : ''}`;
  doc.setFontSize(12);
  doc.setFont('PTSans', 'bold');
  doc.text(title, pageW / 2, 18, { align: 'center' });

  let y = 30;
  drawRow(['Месяц', 'Всего', 'Утверждённых', 'Черновиков'], true, true);
  stats.rows.forEach((r) => {
    drawRow([`${MONTHS_RU[r.month]} ${r.year}`, r.total, r.approved, r.drafts], false, false);
  });
  drawRow(['Итого за период', stats.total.total, stats.total.approved, stats.total.drafts], true, true);
}

export async function exportPlannerPdf(opts) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  await registerPdfFont(doc);
  const sections = opts.sections && opts.sections.length ? opts.sections : null;
  const refGrids = sections ? sections[0].grids : opts.grids;

  if (sections) {
    let firstPage = true;
    sections.forEach((s) => {
      s.grids.forEach((grid, i) => {
        if (!firstPage) doc.addPage('a4', 'landscape');
        renderPdfMonth(doc, grid, s.objectName, i === 0);
        if (firstPage) {
          renderPdfLegend(doc, 198);
          firstPage = false;
        }
      });
    });
    doc.addPage('a4', 'landscape');
    renderPdfStats(doc, computePlannerStats(opts.mergedGrids), opts.objectName, formatMonthsPeriod(refGrids));
  } else {
    opts.grids.forEach((grid, i) => {
      if (i > 0) doc.addPage('a4', 'landscape');
      renderPdfMonth(doc, grid, opts.objectName, i === 0);
      if (i === 0) renderPdfLegend(doc, 198);
    });
    doc.addPage('a4', 'landscape');
    renderPdfStats(doc, computePlannerStats(opts.grids), opts.objectName, formatMonthsPeriod(opts.grids));
  }
  downloadBlob(plannerFilename(refGrids, 'pdf'), doc.output('blob'));
}